import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { PermissionGuard } from '../../guards/permission.guard';
import { PermissionService } from './permission.service';

/**
 * Evicts the user from a page they no longer have permission to view.
 *
 * Angular only runs route guards during navigation, so a permission revoked
 * while the user is sitting on a protected page would otherwise go unnoticed
 * until they navigated. This re-runs the same check the guard uses whenever
 * permissions change, and redirects if the active route is no longer allowed.
 *
 * Only reacts to CHANGES (changes$ skips the replayed initial value) — it must
 * never fire on the value already in place when a page loads, or it would race
 * the guard during normal navigation.
 */
@Injectable({ providedIn: 'root' })
export class PermissionRouteWatcher implements OnDestroy {
  private sub: Subscription | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private permissions: PermissionService,
    private permissionGuard: PermissionGuard,
    private toastr: ToastrService
  ) {}

  start(): void {
    if (this.sub) {
      return;
    }
    this.sub = this.permissions.changes$.subscribe(() => this.recheckActiveRoute());
  }

  stop(): void {
    this.sub?.unsubscribe();
    this.sub = null;
  }

  private recheckActiveRoute(): void {
    // Walk to the deepest activated child — that is the route actually rendered,
    // and the one carrying the most specific data.permission.
    let snapshot: ActivatedRouteSnapshot | null = this.route.snapshot;
    const chain: ActivatedRouteSnapshot[] = [];

    while (snapshot) {
      chain.push(snapshot);
      snapshot = snapshot.firstChild;
    }

    // A parent's permission still gates its children, so check the whole chain.
    const denied = chain.some((node) => !this.permissionGuard.isAllowed(node));
    if (!denied) {
      return;
    }

    this.toastr.warning(
      'Your access to this page was changed by an administrator.',
      'Permissions updated'
    );
    this.router.navigate(['/access-denied']);
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
